import { useState, useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ReceiptDropZone } from './ReceiptDropZone';
import { UploadFailureList } from './UploadFailureList';
import { ReceiptFormPanel } from './ReceiptFormPanel';
import { BatchStepper } from './BatchStepper';
import { useUploadReceipt } from '@/hooks/useUploadReceipt';
import { useCreatePendingReceipt, useDeletePendingReceipt } from '@/hooks/usePendingReceipts';
import { useCreateExpense, useUpdateExpense } from '@/hooks/useExpenses';
import { useToast } from '@/hooks/use-toast';
import { fetchJobFileUrl } from '@/hooks/useJobFiles';
import { JobPhotoPickerDialog } from '@/pages/jobs/components/files/JobPhotoPickerDialog';
import { isHeic, isPdf, receiptKindFromFile } from '@/lib/receipt-file';
import { downscaleForUpload } from '@/lib/image-downscale';
import { UPLOAD_CONCURRENCY, pool } from '@/lib/pool';
import { apiUrl } from '@/api/client';
import type { ExpenseFormData } from './ExpenseForm';
import type { ExpenseWithDetails, JobFileEntry, PendingReceipt } from '@jobos/shared';

/** Receipts a single library pick can start expenses for, matching the drop zone. */
const MAX_LIBRARY_PICK = 20;

type FlowState = 'dropzone' | 'uploading' | 'filling' | 'complete';

/**
 * An object URL for a local preview, or null if the environment won't make one.
 *
 * Never throws: the caller already has the uploaded file's server URL to fall
 * back to, and losing a local preview must not cost the receipt.
 */
function safeObjectUrl(file: File): string | null {
  try {
    return URL.createObjectURL(file);
  } catch {
    return null;
  }
}

interface ReceiptItem {
  previewUrl: string;
  /**
   * Whether `previewUrl` is ours to revoke. An upload previews from a local
   * object URL; a photo picked out of the library previews from a presigned
   * one, and revoking that would be meaningless at best.
   */
  isObjectUrl: boolean;
  /** Which viewer the preview needs — decided when the item is made. */
  kind: 'image' | 'pdf';
  /** The row in the job's file store. What the expense records. */
  fileId: string | null;
  /**
   * The queued receipt this item consumes, so an abandoned batch can clean up
   * after itself. Null for a library pick: the file was already the job's
   * before this expense was started, and stays the job's if it never happens.
   */
  pendingReceiptId: string | null;
}

/**
 * A file that did not make it. Kept on screen with its reason rather than
 * silently dropped, and holding the original File so Retry needs no re-pick.
 */
interface UploadFailure {
  file: File;
  reason: string;
}

interface ExpenseEntryFlowProps {
  jobId: string;
  projects?: { id: string; name: string }[];
  editingExpense?: ExpenseWithDetails | null;
  pendingReceipts?: PendingReceipt[];
  onComplete: () => void;
  onCancel: () => void;
}

export function ExpenseEntryFlow({
  jobId,
  projects,
  editingExpense,
  pendingReceipts: resumePending,
  onComplete,
  onCancel,
}: ExpenseEntryFlowProps) {
  const [flowState, setFlowState] = useState<FlowState>(
    editingExpense ? 'filling' : resumePending?.length ? 'filling' : 'dropzone'
  );
  const [receipts, setReceipts] = useState<ReceiptItem[]>([]);
  const [failures, setFailures] = useState<UploadFailure[]>([]);
  const [pendingItems] = useState<PendingReceipt[]>(resumePending ?? []);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [noReceipt, setNoReceipt] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  // Expenses already persisted in this batch, keyed by their item index. Lets
  // the user go Back to an earlier receipt: a revisited item updates its saved
  // expense instead of creating a duplicate.
  const [savedExpenses, setSavedExpenses] = useState<(ExpenseWithDetails | null)[]>([]);
  // Indices the user chose to skip (defer). They stay in the batch as pending
  // receipts — flagged orange in the stepper — so the user can come back and
  // fill them, or leave them for the pending-receipts banner afterwards.
  const [skipped, setSkipped] = useState<Set<number>>(() => new Set());
  const [uploadProgress, setUploadProgress] = useState({ completed: 0, total: 0 });
  const [pickerOpen, setPickerOpen] = useState(false);
  // A receipt chosen while editing, held until the form is saved. Editing is a
  // single form with a Save at the bottom; swapping the receipt the instant it
  // is picked would be the one change on the screen that already happened.
  const [editPick, setEditPick] = useState<
    { fileId: string; url: string; kind: 'image' | 'pdf' } | null
  >(null);

  const queryClient = useQueryClient();
  const uploadReceipt = useUploadReceipt();
  const createPendingReceipt = useCreatePendingReceipt();
  const createExpense = useCreateExpense({ silent: true });
  const updateExpense = useUpdateExpense(jobId);
  const deletePendingReceipt = useDeletePendingReceipt(jobId);
  const { toast } = useToast();

  // Clean up object URLs on unmount to prevent memory leaks
  const receiptsRef = useRef<ReceiptItem[]>([]);
  receiptsRef.current = receipts;
  useEffect(() => {
    return () => {
      receiptsRef.current.forEach((r) => {
        if (r.isObjectUrl) URL.revokeObjectURL(r.previewUrl);
      });
    };
  }, []);

  // Total items in the batch (new uploads + resumed pending)
  const totalItems = receipts.length + pendingItems.length;
  const isResuming = pendingItems.length > 0 && receipts.length === 0;

  // The next still-unresolved receipt (neither saved nor skipped), searching
  // forward from the current one and wrapping around. Returns -1 when every
  // other receipt is already handled — i.e. the batch is ready to finish.
  const findNextTodo = useCallback((): number => {
    const isTodo = (i: number) => !savedExpenses[i] && !skipped.has(i);
    for (let i = currentIndex + 1; i < totalItems; i++) {
      if (isTodo(i)) return i;
    }
    for (let i = 0; i < currentIndex; i++) {
      if (isTodo(i)) return i;
    }
    return -1;
  }, [currentIndex, totalItems, savedExpenses, skipped]);

  const handleFilesSelected = useCallback(async (incomingFiles: File[]) => {
    setFlowState('uploading');
    setUploadProgress({ completed: 0, total: incomingFiles.length });
    // A retry starts from a clean slate; anything that fails again lands below.
    setFailures([]);

    const succeeded: ReceiptItem[] = [];
    const failed: UploadFailure[] = [];

    await pool(incomingFiles, UPLOAD_CONCURRENCY, async (file) => {
      // Downscaled immediately before its own upload rather than for the whole
      // batch up front, so only one decoded bitmap is ever in flight.
      //
      // heicFallback matters here and not on the job files tab: a receipt batch
      // is a handful of files, so paying 1-3s of libheif per undecodable HEIC is
      // worth not sending full-size bytes to a 1GB VM to decode.
      const { display } = await downscaleForUpload(file, {
        heicFallback: true,
        // A receipt has no archival reader, so the full-size copy would be
        // encoded and discarded.
        withOriginal: false,
      });

      // The preview is of what we actually uploaded. A HEIC that survived the
      // ladder undecoded can't be rendered by an <img>, so there is nothing
      // local worth showing — the server's normalized copy stands in once the
      // upload returns an id.
      const renderable = !isHeic(display);

      // Only the network work goes in the try. Building the preview after it
      // means a failure there can't be reported as an upload failure — which
      // would strand a file that did upload, with a pending receipt pointing at
      // it and nothing on screen referencing either.
      let stored: { fileId: string; pendingReceiptId: string } | null = null;
      try {
        const { fileId } = await uploadReceipt.mutateAsync({ jobId, file: display });
        const pending = await createPendingReceipt.mutateAsync({ jobId, fileId });
        stored = { fileId, pendingReceiptId: pending.id };
      } catch (err) {
        // This file only. The pool keeps pulling, and the file stays on screen
        // with its reason instead of disappearing.
        failed.push({
          file,
          reason: err instanceof Error ? err.message : 'Upload failed',
        });
      } finally {
        setUploadProgress((prev) => ({ ...prev, completed: prev.completed + 1 }));
      }

      if (!stored) return;

      // The server's copy is always a valid fallback preview: it has been
      // normalized to JPEG on the way in. So an object URL is an optimisation,
      // not a requirement, and is allowed to fail.
      const localUrl = renderable ? safeObjectUrl(display) : null;
      succeeded.push({
        previewUrl: localUrl ?? apiUrl(`/pending-receipts/${stored.pendingReceiptId}/image`),
        isObjectUrl: localUrl !== null,
        kind: isPdf(display) ? 'pdf' : 'image',
        ...stored,
      });
    });

    // Append rather than replace. A retry of the failures from a partial batch
    // must not discard the receipts that already uploaded — and must not drop
    // back to the drop zone when every retry fails again.
    setReceipts((prev) => [...prev, ...succeeded]);
    setFailures(failed);
    const total = receiptsRef.current.length + succeeded.length;
    setFlowState(total > 0 ? 'filling' : 'dropzone');
  }, [jobId, uploadReceipt, createPendingReceipt]);

  const handleRetryFailed = useCallback(() => {
    const files = failures.map((f) => f.file);
    if (files.length > 0) void handleFilesSelected(files);
  }, [failures, handleFilesSelected]);

  const handleNoReceipt = useCallback(() => {
    setNoReceipt(true);
    setFlowState('filling');
  }, []);

  /**
   * Photos the user already took, claimed as receipts.
   *
   * Nothing is uploaded and no pending receipt is queued: the row exists, so
   * the only thing missing is the expense in front of it. The presigned URLs
   * are fetched through the same cache the picker's thumbnails filled, so a
   * photo just looked at costs nothing to preview.
   */
  const handleLibraryPicked = useCallback(async (entries: JobFileEntry[]) => {
    if (entries.length === 0) return;

    const kindOf = (entry: JobFileEntry): 'image' | 'pdf' =>
      entry.contentType === 'application/pdf' ? 'pdf' : 'image';

    // Editing swaps this one expense's receipt; there is no batch to start.
    if (editingExpense) {
      const entry = entries[0]!;
      setEditPick({
        fileId: entry.id,
        url: await fetchJobFileUrl(queryClient, jobId, entry.id),
        kind: kindOf(entry),
      });
      return;
    }

    const items = await Promise.all(
      entries.map(async (entry) => ({
        previewUrl: await fetchJobFileUrl(queryClient, jobId, entry.id),
        isObjectUrl: false,
        kind: kindOf(entry),
        fileId: entry.id,
        pendingReceiptId: null,
      })),
    );

    setReceipts(items);
    setCurrentIndex(0);
    setFlowState('filling');
  }, [editingExpense, jobId, queryClient]);

  const getCurrentReceiptUrl = (): string | null => {
    // A receipt picked in this editing session, before it has been saved.
    if (editPick) return editPick.url;
    if (editingExpense?.fileId) {
      return apiUrl(`/expenses/${editingExpense.id}/receipt`);
    }
    // Local preview for freshly uploaded files (still valid even once saved).
    if (receipts[currentIndex]) {
      return receipts[currentIndex].previewUrl;
    }
    // A revisited, already-saved item: its pending receipt is gone, so read
    // the receipt off the expense instead.
    const saved = savedExpenses[currentIndex];
    if (saved?.fileId) {
      return apiUrl(`/expenses/${saved.id}/receipt`);
    }
    if (isResuming && pendingItems[currentIndex]) {
      return apiUrl(`/pending-receipts/${pendingItems[currentIndex].id}/image`);
    }
    return null;
  };

  const getCurrentReceiptKind = (): 'image' | 'pdf' => {
    if (editPick) return editPick.kind;
    if (editingExpense?.fileId) return receiptKindFromFile(editingExpense.file);
    // Decided when the item was made — from the local File for an upload, from
    // the row's content type for a library pick. Either way, no round trip.
    const item = receipts[currentIndex];
    if (item) return item.kind;
    const saved = savedExpenses[currentIndex];
    if (saved?.fileId) return receiptKindFromFile(saved.file);
    if (isResuming && pendingItems[currentIndex]) return receiptKindFromFile(pendingItems[currentIndex].file);
    return 'image';
  };

  const getCurrentPendingReceiptId = (): string | null => {
    if (isResuming && pendingItems[currentIndex]) {
      return pendingItems[currentIndex].id;
    }
    return receipts[currentIndex]?.pendingReceiptId ?? null;
  };

  const getCurrentFileId = (): string | null => {
    if (isResuming && pendingItems[currentIndex]) {
      return pendingItems[currentIndex].fileId;
    }
    return receipts[currentIndex]?.fileId ?? null;
  };

  const handleFormSubmit = useCallback(async (data: ExpenseFormData) => {
    // Edit path: update existing expense
    if (editingExpense) {
      await updateExpense.mutateAsync({
        id: editingExpense.id,
        categoryId: data.categoryId,
        projectName: data.projectName || null,
        storeName: data.storeName,
        description: data.description,
        amount: Number(data.amount),
        expenseDate: data.expenseDate,
        paidDate: data.paidDate || null,
        paymentMethod: data.paymentMethod,
        checkNumber: data.paymentMethod === 'check' ? (data.checkNumber || null) : null,
        reimburse: data.reimburse ?? false,
        // Sent only when this session picked one. Absent leaves the receipt
        // where it was; null is what detaches one, and nothing here asks for it.
        ...(editPick ? { fileId: editPick.fileId } : {}),
      });
      onComplete();
      return;
    }

    // Batch path. If this item was already saved (the user came Back to it),
    // update its expense; otherwise create a new one. Either way, remember the
    // saved expense by index so a later Back revisit edits rather than dupes.
    const existing = savedExpenses[currentIndex] ?? null;
    let createdCount = savedCount;

    if (existing) {
      const updated = await updateExpense.mutateAsync({
        id: existing.id,
        categoryId: data.categoryId,
        projectName: data.projectName || null,
        storeName: data.storeName,
        description: data.description,
        amount: Number(data.amount),
        expenseDate: data.expenseDate,
        paidDate: data.paidDate || null,
        paymentMethod: data.paymentMethod,
        checkNumber: data.paymentMethod === 'check' ? (data.checkNumber || null) : null,
        reimburse: data.reimburse ?? false,
      });
      setSavedExpenses((prev) => {
        const next = [...prev];
        next[currentIndex] = updated;
        return next;
      });
    } else {
      const fileId = getCurrentFileId();
      const pendingId = getCurrentPendingReceiptId();
      const created = await createExpense.mutateAsync({
        jobId,
        categoryId: data.categoryId,
        projectName: data.projectName || null,
        storeName: data.storeName,
        description: data.description,
        amount: Number(data.amount),
        expenseDate: data.expenseDate,
        paidDate: data.paidDate || null,
        paymentMethod: data.paymentMethod,
        checkNumber: data.paymentMethod === 'check' ? (data.checkNumber || null) : null,
        reimburse: data.reimburse ?? false,
        fileId,
        pendingReceiptId: pendingId ?? undefined,
      });
      setSavedExpenses((prev) => {
        const next = [...prev];
        next[currentIndex] = created;
        return next;
      });
      createdCount = savedCount + 1;
      setSavedCount(createdCount);
    }

    // Saving clears any prior skip flag for this item.
    setSkipped((prev) => {
      if (!prev.has(currentIndex)) return prev;
      const next = new Set(prev);
      next.delete(currentIndex);
      return next;
    });

    const next = findNextTodo();
    if (next === -1) {
      toast({
        title: noReceipt ? 'Expense added' : `${createdCount} expense${createdCount !== 1 ? 's' : ''} added`,
      });
      onComplete();
    } else {
      setCurrentIndex(next);
    }
  }, [jobId, editingExpense, currentIndex, noReceipt, savedCount, savedExpenses, createExpense, updateExpense, findNextTodo, toast, onComplete]);

  const handleBack = useCallback(() => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  }, []);

  // Defer the current receipt: flag it skipped and move on, but keep it in the
  // batch so the user can return to it. Skipping the last item ends the flow —
  // the skipped (still-pending) receipts remain for the pending banner.
  const handleSkip = useCallback(() => {
    setSkipped((prev) => {
      const next = new Set(prev);
      next.add(currentIndex);
      return next;
    });
    const next = findNextTodo();
    if (next === -1) {
      if (savedCount > 0) {
        toast({ title: `${savedCount} expense${savedCount !== 1 ? 's' : ''} added` });
      }
      onComplete();
    } else {
      setCurrentIndex(next);
    }
  }, [currentIndex, findNextTodo, savedCount, toast, onComplete]);

  // Jump straight to any receipt via the step indicator.
  const handleJump = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  // Advance without saving — used when revisiting an already-saved item that the
  // user left unchanged, so "Next" doesn't fire a redundant update.
  const handleAdvance = useCallback(() => {
    const next = findNextTodo();
    if (next === -1) {
      if (savedCount > 0) {
        toast({ title: `${savedCount} expense${savedCount !== 1 ? 's' : ''} added` });
      }
      onComplete();
    } else {
      setCurrentIndex(next);
    }
  }, [findNextTodo, savedCount, toast, onComplete]);

  const deleteRemainingPendingReceipts = useCallback(async () => {
    // Delete every pending receipt that was never turned into an expense.
    // Driven by savedExpenses (not currentIndex) so Back-navigation can't leave
    // a saved item's already-consumed receipt in the delete set, nor skip an
    // unsaved one that sits before the current position.
    const remaining: string[] = [];
    for (let i = 0; i < receipts.length; i++) {
      if (savedExpenses[i]) continue;
      const id = receipts[i]?.pendingReceiptId;
      if (id) remaining.push(id);
    }
    if (isResuming) {
      for (let i = 0; i < pendingItems.length; i++) {
        if (savedExpenses[i]) continue;
        remaining.push(pendingItems[i]!.id);
      }
    }
    // Fire-and-forget deletions in parallel
    await Promise.allSettled(
      remaining.map((id) => deletePendingReceipt.mutateAsync(id))
    );
  }, [receipts, pendingItems, isResuming, savedExpenses, deletePendingReceipt]);

  const handleCancelBatch = useCallback(async () => {
    await deleteRemainingPendingReceipts();
    if (savedCount > 0) {
      toast({
        title: `${savedCount} expense${savedCount !== 1 ? 's' : ''} saved`,
      });
    }
    onComplete();
  }, [savedCount, toast, onComplete, deleteRemainingPendingReceipts]);

  const handleCancel = useCallback(async () => {
    if (noReceipt || editingExpense) {
      onCancel();
    } else {
      await handleCancelBatch();
    }
  }, [noReceipt, editingExpense, onCancel, handleCancelBatch]);

  // One dialog for both entry points, so the picking rules are stated once.
  // A photo that already backs an expense stays choosable — a single receipt
  // covering two line items is ordinary — but says so.
  const picker = (
    <JobPhotoPickerDialog
      jobId={jobId}
      open={pickerOpen}
      onOpenChange={setPickerOpen}
      isUsed={(file) => file.hasExpense}
      usedLabel="Already an expense"
      description={
        editingExpense
          ? 'Photos already on this job. The one you pick becomes this expense’s receipt when you save.'
          : 'Photos already on this job. Pick the receipts you want to enter — each one starts an expense.'
      }
      maxSelection={editingExpense ? 1 : MAX_LIBRARY_PICK}
      onConfirm={(entries) => {
        void handleLibraryPicked(entries);
      }}
    />
  );

  if (flowState === 'dropzone' || flowState === 'uploading') {
    return (
      <>
        <UploadFailureList
          failures={failures}
          onRetry={handleRetryFailed}
          onDismiss={() => setFailures([])}
          busy={flowState === 'uploading'}
        />
        <ReceiptDropZone
          onFilesSelected={handleFilesSelected}
          onNoReceipt={handleNoReceipt}
          onChooseFromLibrary={() => setPickerOpen(true)}
          isUploading={flowState === 'uploading'}
          uploadProgress={uploadProgress}
        />
        {picker}
      </>
    );
  }

  // "Finish" only when saving the current item leaves nothing unresolved —
  // every other receipt is already saved or intentionally skipped.
  const willFinish = findNextTodo() === -1;
  const isSaving = createExpense.isPending || updateExpense.isPending;

  // The expense backing the current item: the single-edit prop, or — in a batch
  // — whatever was saved at this index (set when revisiting via Back).
  const currentSaved = savedExpenses[currentIndex] ?? null;
  const effectiveEditing = editingExpense ?? currentSaved;
  const canGoBack = !editingExpense && !noReceipt && currentIndex > 0;
  const isBatch = !noReceipt && !editingExpense;
  // The stepper renders (and carries its own Cancel) only for multi-item batches.
  // Hide the footer Cancel in that case so there's a single, unambiguous Cancel.
  const hasStepperCancel = isBatch && totalItems > 1;
  // Skip only makes sense when there's another receipt to move on to.
  const canSkip = isBatch && totalItems > 1;

  // Per-receipt status for the stepper: saved (green), skipped (orange), or todo.
  const stepStatuses: ('saved' | 'skipped' | 'todo')[] = Array.from(
    { length: totalItems },
    (_, i) => (savedExpenses[i] ? 'saved' : skipped.has(i) ? 'skipped' : 'todo')
  );

  return (
    <>
      {/* Above the form, not inside it: a partial batch shows the receipts that
          did land AND the ones that didn't, and the failures must not be buried
          under the form the successes opened. */}
      <UploadFailureList
        failures={failures}
        onRetry={handleRetryFailed}
        onDismiss={() => setFailures([])}
      />
    {/* `overflow-hidden` is what rounds the corners over the receipt band, but it
        also makes this box the scroll container for anything sticky inside it —
        which would strand the form's footer. Below `sm` the corners lose and the
        footer wins. */}
    <div className="mb-4 rounded-lg border max-sm:rounded-none sm:overflow-hidden">
      {picker}
      <BatchStepper
        total={noReceipt ? 0 : totalItems}
        currentIndex={currentIndex}
        statuses={stepStatuses}
        onJump={handleJump}
        onCancelBatch={handleCancelBatch}
      />
      <ReceiptFormPanel
        // Remount on revisited (saved) items so the form prefills their values;
        // a stable 'new' key across fresh items preserves field carry-over.
        key={effectiveEditing ? `exp-${effectiveEditing.id}` : 'new'}
        receiptPreviewUrl={noReceipt ? null : getCurrentReceiptUrl()}
        receiptKind={noReceipt ? 'image' : getCurrentReceiptKind()}
        projects={projects}
        editingExpense={effectiveEditing}
        onSubmit={handleFormSubmit}
        onCancel={handleCancel}
        // Editing only. Mid-batch the receipt is the reason the form is open,
        // and swapping it there would orphan the pending receipt behind it.
        onChooseFromLibrary={editingExpense ? () => setPickerOpen(true) : undefined}
        showCancel={!hasStepperCancel}
        onBack={canGoBack ? handleBack : undefined}
        onSkip={canSkip ? handleSkip : undefined}
        onAdvance={handleAdvance}
        isPending={isSaving}
        batchPosition={isBatch ? { isSaved: !!currentSaved, isLast: willFinish } : undefined}
      />
    </div>
    </>
  );
}
